import { ChevronRightIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import pushRoute from '../../hooks/use-push-route'
import { changeSidebarAction } from '../../redux/actions/changeSidebarAction'
import { changeSidebarSubMenus } from '../../redux/actions/changeSideBarSubMenus'
import { equal, ternary } from '../../util/javascript'
import Flex from '../Flex'
import { sideBarMenuList } from './menuItem'
import SidebarSocialLoopPromo from './SidebarSocialLoopPromo'

const getMenuKey = (item) => item.menuKey || item.name

const subItemClass = (active, depth = 1) =>
  classNames(
    active ? 'text-white bg-gray-50' : 'text-light-coffee bg-gray-50-hover',
    'block rounded-md py-2 pr-2 text-base leading-6 w-full text-left',
    depth === 1 ? 'pl-9' : 'pl-14',
  )

const Ul = ({ mapList = [] }) => {
  const dispatch = useDispatch()
  const path = useSelector((state) => state.auth.path)
  const openSubmenus = useSelector((state) => state.connectStore?.openSubmenus)
  const { routes } = pushRoute()

  const topItems = mapList.filter((item) => item.name !== 'More')
  const bottomItems = mapList.filter((item) => item.name === 'More')

  const setSubmenuOpen = (itemName, open) => {
    dispatch(
      changeSidebarSubMenus({
        ...openSubmenus,
        [itemName]: open,
      }),
    )
  }

  const handleSubmenuClick = (itemName) => {
    setSubmenuOpen(itemName, !openSubmenus[itemName])
  }

  useEffect(() => {
    const menusToOpen = new Set()

    const visit = (items) => {
      items.forEach((item) => {
        if (!item.children) return

        const key = getMenuKey(item)
        item.children.forEach((child) => {
          if (child.to === path) {
            menusToOpen.add(key)
          }
          if (child.children) {
            const childKey = getMenuKey(child)
            const nestedMatch = child.children.some(
              (nested) => nested.to === path,
            )
            if (nestedMatch) {
              menusToOpen.add(key)
              menusToOpen.add(childKey)
            }
            visit([child])
          }
        })
      })
    }

    visit(mapList)

    if (menusToOpen.size > 0) {
      const updated = { ...openSubmenus }
      menusToOpen.forEach((key) => {
        updated[key] = true
      })
      dispatch(changeSidebarSubMenus(updated))
    }
  }, [])

  const handleClick = (to) => {
    dispatch(changeSidebarAction(false))
    routes(to, false)
  }

  const renderSubMenuItem = (subItem, depth = 1) => {
    if (subItem.type === 'button') {
      return (
        <button
          type="button"
          onClick={subItem.onClick}
          className={subItemClass(false, depth)}
        >
          {subItem.name}
        </button>
      )
    }

    if (subItem.isExternal) {
      return (
        <a href={subItem.to} className={subItemClass(false, depth)}>
          {subItem.name}
        </a>
      )
    }

    if (subItem.isMonolith) {
      return (
        <a
          href={subItem.to}
          className={subItemClass(false, depth)}
          onClick={(event) => {
            event.preventDefault()
            dispatch(changeSidebarAction(false))
            window.location.assign(subItem.to)
          }}
        >
          {subItem.name}
        </a>
      )
    }

    if (subItem.isLink) {
      return (
        <a
          href={subItem.to}
          {...(subItem.newTab
            ? { target: '_blank', rel: 'noopener noreferrer' }
            : {})}
          className={subItemClass(false, depth)}
        >
          {subItem.name}
        </a>
      )
    }

    if (subItem.children) {
      const key = getMenuKey(subItem)
      return (
        <div>
          <button
            type="button"
            onClick={() => handleSubmenuClick(key)}
            className={classNames(
              subItemClass(false, depth),
              'group flex items-center font-semibold text-white',
            )}
          >
            {subItem.name}
            <ChevronRightIcon
              className={classNames(
                openSubmenus[key]
                  ? 'rotate-90 text-white'
                  : 'text-gray-50 text-gray-50-hover',
                'h-5 w-5 shrink-0 ml-auto',
              )}
              aria-hidden="true"
            />
          </button>
          {openSubmenus[key] && (
            <ul className="flex flex-col gap-y-1">
              {subItem.children.map((nested) => (
                <li key={nested.name}>
                  {renderSubMenuItem(nested, depth + 1)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )
    }

    return (
      <Link
        onClick={() => handleClick(subItem.to)}
        to={subItem.to}
        className={subItemClass(equal(path, subItem.to), depth)}
      >
        {subItem.name}
      </Link>
    )
  }

  const renderMenuList = (menuList) => (
    <ul className="flex flex-col gap-y-2">
      {menuList.map((item) => (
        <li key={`${item.name}-${item.to || 'button'}`}>
          {ternary(
            item.type === 'button',
            <div
              className={classNames(
                ternary(equal(path, item.to), '', 'bg-gray-50-hover'),
                'group flex gap-x-3 rounded-md p-2 text-base leading-6 font-semibold text-white cursor-pointer',
              )}
              onClick={item.onClick}
              role="presentation"
            >
              <item.icon
                className="h-6 w-6 shrink-0 text-gray-400"
                aria-hidden="true"
              />
              <span>{item.name}</span>
            </div>,
            ternary(
              !item.children,
              <Link
                onClick={() => handleClick(item.to)}
                to={item.to}
                className={classNames(
                  ternary(
                    equal(path, item.to),
                    'bg-gray-50',
                    'bg-gray-50-hover',
                  ),
                  'group flex gap-x-3 rounded-md p-2 text-base leading-6 font-semibold text-white',
                )}
              >
                <item.icon
                  className="h-6 w-6 shrink-0 text-gray-400"
                  aria-hidden="true"
                />
                {item.name}
              </Link>,
              <div>
                <button
                  type="button"
                  onClick={() => handleSubmenuClick(getMenuKey(item))}
                  className="group flex items-center w-full text-left rounded-md p-2 gap-x-3 text-base leading-6 font-semibold text-white"
                >
                  <item.icon
                    className="h-6 w-6 shrink-0 text-gray-400"
                    aria-hidden="true"
                  />
                  {item.name}
                  <ChevronRightIcon
                    className={classNames(
                      openSubmenus[getMenuKey(item)]
                        ? 'rotate-90 text-white'
                        : 'text-gray-50 text-gray-50-hover',
                      'h-5 w-5 shrink-0 ml-auto',
                    )}
                    aria-hidden="true"
                  />
                </button>
                {openSubmenus[getMenuKey(item)] && (
                  <ul className="mt-1 ml-1.5 px-2 flex flex-col gap-y-1">
                    {item.children.map((subItem) => (
                      <li key={subItem.name}>{renderSubMenuItem(subItem)}</li>
                    ))}
                  </ul>
                )}
              </div>,
            ),
          )}
        </li>
      ))}
    </ul>
  )

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full border-gray-200 bg-coffee text-light-coffee">
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-4 pb-4">
        <div className="flex flex-col gap-y-4">
          {renderMenuList(topItems)}
          <SidebarSocialLoopPromo />
          {bottomItems.length > 0 && renderMenuList(bottomItems)}
        </div>
      </div>
    </div>
  )
}

const Index = () => (
  <Flex extraClass="bg-coffee w-full h-full min-h-0 flex-col side-nav">
    <Ul mapList={sideBarMenuList} />
  </Flex>
)

export default Index
